import {
  useKineticMap,
  useGetNodes,
  MapContext,
  DEFAULT_ZOOM,
  MapSearchResultItem,
  useMapBoxSearchContext,
  useMapSearchBoxPin,
  MapSearchResultProps,
  useAppSearchHistory,
  publicUrl,
  AppSearchItemType,
  AppSearchIconKey,
} from '@kinetic-ui/shared';
import { useCamerasListQuery } from '@kinetic-ui/data-layer';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import VideoIcon from '@mui/icons-material/Videocam';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../EntityList/consts';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const map = useKineticMap();
  const { searchText } = useMapBoxSearchContext();
  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();

  const result = useCamerasListQuery({
    fetchPolicy: 'cache-and-network',
    variables: { searchText, limit: 5 },
  });

  const cameras = useGetNodes(result?.data?.cameras);
  const { push } = useHistory();
  const { addSearchHistory } = useAppSearchHistory();

  const onClick = useCallback(
    (id: string, location?: any, openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const camera = cameras.find((e) => e.id === id);
        if (camera?.name) {
          addSearchHistory({
            name: camera.name,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.Video,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin({ type: 'Point', coordinates: location?.coordinates ?? [0, 0] });
        return;
      }
      !selectedLayers.includes('cameras') && setSelectedLayers([...selectedLayers, 'cameras']);
      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      map.setViewState({
        latitude: location?.coordinates?.[1] ?? 0,
        longitude: location?.coordinates?.[0] ?? 0,
        zoom: DEFAULT_ZOOM,
      });
    },
    [
      addMapPin,
      addSearchHistory,
      cameras,
      hasEntityDrawer,
      isAppSearch,
      map,
      onResultClick,
      push,
      selectedLayers,
      setSelectedLayers,
    ]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {cameras.map((camera) => (
        <MapSearchResultItem
          key={`searchItem_${camera.id}`}
          name={camera.name}
          onClick={(openInNewWindow) => onClick(camera.id, camera.location, openInNewWindow)}
          icon={<VideoIcon />}
          newWindow={isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
